import React, { useState, useEffect, useCallback } from 'react';
import { signInWithPopup, signInWithRedirect, getRedirectResult, GoogleAuthProvider, signInWithPhoneNumber, RecaptchaVerifier, signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase';
import NameEntry from './NameEntry';
import RoleSelect from './RoleSelect';
import ArtistUpload from './ArtistUpload';
import ArtistUpiForm from './ArtistUpiForm';
import ProfilePhotoSetup from './ProfilePhotoSetup';
import './SignUp.css';
import { getUserById } from '../services/userService';
import { useNavigate, Link } from 'react-router-dom';

function SignUp({ onComplete }) {
    const navigate = useNavigate();
    const [method, setMethod] = useState('signup');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [fullName, setFullName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [otp, setOtp] = useState('');
    const [verificationId, setVerificationId] = useState('');
    const [error, setError] = useState(null);
    const [authInProgress, setAuthInProgress] = useState(false);
    const [user, setUser] = useState(null);
    const [showNameEntry, setShowNameEntry] = useState(false);
    const [showRoleSelect, setShowRoleSelect] = useState(false);
    const [showArtistUpload, setShowArtistUpload] = useState(false);
    const [showArtistUpiEntry, setShowArtistUpiEntry] = useState(false);
    const [showProfilePhotoSetup, setShowProfilePhotoSetup] = useState(false);
    const [checkingProfile, setCheckingProfile] = useState(false);

    useEffect(() => {
        // Clean up reCAPTCHA on unmount
        return () => {
            if (window.recaptchaVerifier) {
                window.recaptchaVerifier.clear();
                window.recaptchaVerifier = null;
            }
            // Clear any pending auth operations
            setError(null);
        };
    }, []);

    // catch unhandled promise rejections from firebase popup internals
    useEffect(() => {
        const onUnhandled = (evt) => {
            try {
                const reason = evt.reason || '';
                const message = (reason && reason.message) ? reason.message : String(reason);
                if (message && message.includes('Pending promise was never set')) {
                    console.error('Caught firebase internal pending-promise error:', reason);
                    setError('Authentication interrupted. Please try again.');
                    setAuthInProgress(false);
                    // prevent default logging in console
                    // Note: some environments may ignore preventDefault here
                    if (evt.preventDefault) evt.preventDefault();
                }
            } catch (e) {
                // ignore
            }
        };

        window.addEventListener('unhandledrejection', onUnhandled);
        return () => window.removeEventListener('unhandledrejection', onUnhandled);
    }, []);

    // Check if user profile exists in Firestore
    const checkUserProfile = useCallback(async (authUser) => {
        try {
            setCheckingProfile(true);
            const existingProfile = await getUserById(authUser.uid);
            
            if (!existingProfile) {
                navigate('/setup-name', { replace: true });
                return;
            }
            
            // Document exists, check if it's complete
            const hasName = existingProfile.name && existingProfile.name.trim() !== '';
            const hasRole = existingProfile.role;
            
            if (hasName && hasRole) {
                // User already has complete profile (name + role), navigate to home
                console.log('User profile is complete, navigating to home');
                if (onComplete) onComplete();
                navigate('/', { replace: true });
            } else {
                if (hasName && !hasRole) {
                    sessionStorage.setItem('setupDisplayName', existingProfile.name.trim());
                    navigate('/select-role', { replace: true });
                } else {
                    navigate('/setup-name', { replace: true });
                }
            }
        } catch (err) {
            console.error('Error checking user profile:', err);
            // If error checking profile, redirect to name entry page
            navigate('/setup-name', { replace: true });
        } finally {
            setCheckingProfile(false);
            setAuthInProgress(false);
        }
    }, [navigate, onComplete]);

    // Handle redirect results (fallback when popups are blocked)
    useEffect(() => {
        const mountedRef = { current: true };
        (async () => {
            try {
                const result = await getRedirectResult(auth);
                if (result && result.user && mountedRef.current) {
                    console.log('Redirect sign-in result:', result.user);
                    await checkUserProfile(result.user);
                }
            } catch (err) {
                // Ignore "no redirect result" empty-case, log others
                if (err && err.code !== 'auth/no-auth-event') {
                    console.error('Redirect result error:', err);
                    setError(err.message || err.code);
                }
                setAuthInProgress(false);
            }
        })();

        return () => {
            mountedRef.current = false;
        };
    }, [checkUserProfile]);

    // Setup reCAPTCHA verifier
    const setupRecaptcha = () => {
        try {
            if (!window.recaptchaVerifier) {
                window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
                    size: 'normal',
                    callback: () => {
                        setError(null);
                    },
                    'expired-callback': () => {
                        setError('reCAPTCHA has expired. Please solve it again.');
                    }
                });
            }
        } catch (error) {
            setError(error.message);
            console.error('reCAPTCHA Error:', error);
        }
    };

    const handleGoogleSignIn = async () => {
        if (authInProgress) {
            return;
        }
        setAuthInProgress(true);
        try {
            setError(null);
            const provider = new GoogleAuthProvider();
            provider.addScope('email');
            provider.addScope('profile');

            const result = await signInWithPopup(auth, provider);
            console.log('Google Sign-in successful:', result.user);
            await checkUserProfile(result.user);
        } catch (error) {
            console.error('Google Sign-in Error:', error);

            if (error.code === 'auth/popup-closed-by-user') {
                setError('Sign-in was cancelled. Please try again.');
            } else if (error.code === 'auth/popup-blocked') {
                setError('Popup was blocked. Please allow popups and try again.');
            } else if (error.code === 'auth/cancelled-popup-request') {
                setError('Sign-in was cancelled. Please try again.');
            } else {
                setError(error.message || 'An error occurred during sign-in.');
            }

            const fallbackCodes = new Set(['auth/popup-blocked', 'auth/cancelled-popup-request']);
            if (error && fallbackCodes.has(error.code)) {
                try {
                    await signInWithRedirect(auth, new GoogleAuthProvider());
                    return;
                } catch (redirectErr) {
                    console.error('Redirect fallback error:', redirectErr);
                }
            }
            setAuthInProgress(false);
        }
    };

    const handleEmailSignIn = async (e) => {
        e && e.preventDefault();
        if (authInProgress) return;
        setAuthInProgress(true);
        try {
            setError(null);
            if (!email || !password) {
                setError('Please enter both email and password to sign in.');
                setAuthInProgress(false);
                return;
            }
            const result = await signInWithEmailAndPassword(auth, email, password);
            console.log('Email sign-in successful:', result.user);
            await checkUserProfile(result.user);
        } catch (err) {
            console.error('Email sign-in error', err);
            setError(err.message || 'Failed to sign in with email.');
            setAuthInProgress(false);
        }
    };

    // Handle Phone Number Verification
    const handlePhoneSubmit = async (e) => {
        e.preventDefault();
        try {
            setError(null);
            setupRecaptcha();
            const formattedPhone = phoneNumber.startsWith('+') ? phoneNumber : `+91${phoneNumber}`;

            if (!window.recaptchaVerifier) {
                throw new Error('reCAPTCHA not initialized properly');
            }

            const confirmationResult = await signInWithPhoneNumber(
                auth,
                formattedPhone,
                window.recaptchaVerifier
            );
            setVerificationId(confirmationResult);
            setError(null);
        } catch (error) {
            setError(error.message);
            console.error('Phone Verification Error:', error);
        }
    };

    // Handle OTP Verification
    const handleOtpSubmit = async (e) => {
        e.preventDefault();
        if (authInProgress) return;
        setAuthInProgress(true);
        try {
            setError(null);
            if (!verificationId) {
                throw new Error('Please get OTP first');
            }
            const result = await verificationId.confirm(otp);
            console.log('Phone verification successful:', result.user);
            await checkUserProfile(result.user);
        } catch (error) {
            console.error('Error:', error);
            setError(error.message || 'Failed to verify OTP.');
            setAuthInProgress(false);
        }
    };

    const handleNameEntryComplete = () => {
        // After name entry, move to role selection
        setShowNameEntry(false);
        setShowRoleSelect(true);
    };

    const handleRoleComplete = (role) => {
        setShowRoleSelect(false);
        setShowProfilePhotoSetup(true);
        console.log('Profile role selected:', role);
    };

    const handleProfilePhotoComplete = () => {
        setShowProfilePhotoSetup(false);
        if (onComplete) onComplete();
        navigate('/');
    };

    const handleUpiComplete = () => {
        setShowArtistUpiEntry(false);
        setShowArtistUpload(true);
    };

    const handleUploadComplete = (action) => {
        console.log('Upload action:', action);
        // After ArtistUpload (whether uploaded or skipped), profile is complete
        setShowArtistUpload(false);
        if (onComplete) onComplete();
        // Navigate to home after completing onboarding
        navigate('/');
    };

    // Show loading state while checking profile
    if (checkingProfile) {
        return (
            <div className="signup-container">
                <div className="logo-section">
                    <div className="logo-container">
                        <span className="music-icon" aria-hidden="true">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                <path d="M12 3v10.55A4 4 0 1 0 14 17V7h4V3h-6z" />
                            </svg>
                        </span>
                        <h1 className="logo-text">HubTune</h1>
                    </div>
                    <p className="tagline">Your Music Hub</p>
                </div>
                <div className="signup-box">
                    <p>Loading...</p>
                </div>
            </div>
        );
    }

    // Show components based on flow state
    if (showArtistUpload && user) {
        return <ArtistUpload user={user} onComplete={handleUploadComplete} />;
    }

    if (showProfilePhotoSetup && user) {
        return (
            <ProfilePhotoSetup
                user={user}
                onComplete={handleProfilePhotoComplete}
                onBack={() => {
                    setShowProfilePhotoSetup(false);
                    setShowRoleSelect(true);
                }}
            />
        );
    }

    if (showArtistUpiEntry && user) {
        return (
            <ArtistUpiForm
                user={user}
                heading="Enter Your Artist UPI"
                subheading="Tips will be transferred directly to this handle."
                onComplete={handleUpiComplete}
                onBack={() => {
                    setShowArtistUpiEntry(false);
                    setShowRoleSelect(true);
                }}
            />
        );
    }

    if (showRoleSelect && user) {
        return <RoleSelect user={user} onComplete={handleRoleComplete} />;
    }

    if (showNameEntry && user) {
        return <NameEntry user={user} onComplete={handleNameEntryComplete} />;
    }

    return (
        <div className="signup-container">
            <div className="logo-section">
                <div className="logo-container">
                    <span className="music-icon" aria-hidden="true">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path d="M12 3v10.55A4 4 0 1 0 14 17V7h4V3h-6z" />
                        </svg>
                    </span>
                    <h1 className="logo-text">HubTune</h1>
                </div>
                <p className="tagline">Your Music Hub</p>
            </div>
            <div className="signup-box">
                <h1 className="signup-title">Sign Up</h1>

                {error && (
                    <div className="error-message">
                        {error}
                    </div>
                )}

                <div className="auth-methods">
                    <button
                        className={`auth-method-btn ${method === 'login' ? 'active' : ''}`}
                        onClick={() => setMethod('login')}
                    >
                        Login
                    </button>
                    <button
                        className={`auth-method-btn ${method === 'signup' ? 'active' : ''}`}
                        onClick={() => setMethod('signup')}
                    >
                        Sign Up
                    </button>
                    <button
                        className={`auth-method-btn ${method === 'phone' ? 'active' : ''}`}
                        onClick={() => setMethod('phone')}
                    >
                        Phone
                    </button>
                </div>

                {method === 'login' && (
                    <form className="auth-form" onSubmit={(e) => e.preventDefault()}>
                        <div className="form-group">
                            <input
                                type="email"
                                placeholder="Enter your email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="input-field"
                            />
                        </div>
                        <div className="form-group" style={{ marginTop: 8 }}>
                            <input
                                type="password"
                                placeholder="Enter your password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="input-field"
                            />
                        </div>
                        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                            <button type="button" className="submit-btn" onClick={handleEmailSignIn} disabled={authInProgress}>
                                {authInProgress ? 'Signing in...' : 'Sign in'}
                            </button>
                        </div>
                    </form>
                )}

                {method === 'signup' && (
                    <form className="auth-form" onSubmit={(e) => e.preventDefault()}>
                        <div className="form-group">
                            <input
                                type="text"
                                placeholder="Full name"
                                value={fullName}
                                onChange={(e) => setFullName(e.target.value)}
                                className="input-field"
                            />
                        </div>
                        <div className="form-group">
                            <input
                                type="email"
                                placeholder="Email address"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="input-field"
                            />
                        </div>
                        <div className="form-group">
                            <input
                                type="password"
                                placeholder="Password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="input-field"
                            />
                        </div>
                        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                            <button type="button" className="submit-btn" onClick={async () => {
                                // inline signup: create auth user only, profile will be created after NameEntry and RoleSelect
                                if (authInProgress) return;
                                setAuthInProgress(true);
                                try {
                                    setError(null);
                                    if (!fullName || !email || !password) {
                                        setError('Please enter name, email and password to create account.');
                                        setAuthInProgress(false);
                                        return;
                                    }
                                    const result = await createUserWithEmailAndPassword(auth, email, password);
                                    setUser(result.user);
                                    navigate('/setup-name', { replace: true });
                                    setAuthInProgress(false);
                                } catch (err) {
                                    console.error('Inline sign-up error', err);
                                    setError(err.message || 'Failed to create account.');
                                    setAuthInProgress(false);
                                }
                            }} disabled={authInProgress}>
                                {authInProgress ? 'Please wait...' : 'Create account'}
                            </button>
                        </div>
                    </form>
                )}

                {method === 'phone' && !verificationId && (
                    <form className="auth-form" onSubmit={handlePhoneSubmit}>
                        <div className="form-group">
                            <input
                                type="tel"
                                placeholder="Enter phone number"
                                value={phoneNumber}
                                onChange={(e) => setPhoneNumber(e.target.value)}
                                className="input-field"
                            />
                        </div>
                        <div id="recaptcha-container"></div>
                        <button type="submit" className="submit-btn">
                            Get OTP
                        </button>
                    </form>
                )}

                {method === 'phone' && verificationId && (
                    <form className="auth-form" onSubmit={handleOtpSubmit}>
                        <div className="form-group">
                            <input
                                type="text"
                                placeholder="Enter OTP"
                                value={otp}
                                onChange={(e) => setOtp(e.target.value)}
                                className="input-field"
                            />
                        </div>
                        <button type="submit" className="submit-btn">
                            Verify OTP
                        </button>
                    </form>
                )}

                <div className="divider">
                    <span>OR</span>
                </div>

                <button onClick={handleGoogleSignIn} className="google-btn" disabled={authInProgress}>
                    {authInProgress ? (
                        'Signing in...'
                    ) : (
                        <>
                            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" />
                            Continue with Google
                        </>
                    )}
                </button>

                <footer className="signup-footer">
                    <div style={{ marginBottom: '1rem' }}>
                        <Link to="/about" style={{ 
                            display: 'inline-block', 
                            padding: '0.6rem 1.2rem', 
                            background: 'rgba(255, 0, 0, 0.1)', 
                            border: '1px solid rgba(255, 0, 0, 0.3)', 
                            borderRadius: '8px',
                            color: '#fff',
                            textDecoration: 'none',
                            fontWeight: '600',
                            transition: 'all 0.2s ease'
                        }} onMouseEnter={(e) => {
                            e.target.style.background = 'rgba(255, 0, 0, 0.2)';
                            e.target.style.borderColor = 'rgba(255, 0, 0, 0.5)';
                        }} onMouseLeave={(e) => {
                            e.target.style.background = 'rgba(255, 0, 0, 0.1)';
                            e.target.style.borderColor = 'rgba(255, 0, 0, 0.3)';
                        }}>
                            Product
                        </Link>
                    </div>
                    <div>
                        <Link to="/about">About</Link>
                        <span className="footer-sep">·</span>
                        <Link to="/privacy">Privacy</Link>
                        <span className="footer-sep">·</span>
                        <Link to="/terms">Terms</Link>
                        <span className="footer-sep">·</span>
                        <Link to="/refund">Refund</Link>
                        <span className="footer-sep">·</span>
                        <Link to="/artist-payout-kyc-policy">Artist Payout &amp; KYC</Link>
                        <span className="footer-sep">·</span>
                        <Link to="/contact">Contact</Link>
                    </div>
                </footer>
            </div>
        </div>
    );
}

export default SignUp;